import { useCallback, useEffect, useMemo, useRef } from 'react';
import { OnboardingProTask } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/onboarding.pb';
import { useOnboardingShallowStore, useRegisterOnboardingFlow } from '@frontend/product-onboarding';
import { useEligibility, usePreference } from '@frontend/user-preferences';
import { useMatch } from '@frontend/weave-router';
import { CI_PRO_CHECKLIST_POLICY, CI_PRO_FEATURE_IDS, CALL_INTEL_PATH } from '../constant';
import { useProOnboardingEligibility } from '../hooks/use-pro-onboarding-eligibility';
import { useProOnboardingProgress } from '../hooks/use-pro-onboarding-progress';
import { CI_PRO_ONBOARDING_EVENTS, trackOnboardingEvent } from '../tracking';

/**
 * Registers the CI Pro onboarding flow.
 * Mount once in the app shell.
 *
 * Important:
 * - This makes the Get Started checklist accessible globally.
 * - It does NOT auto-open the checklist by itself.
 */
export function CIProOnboarding() {
  const isOnCallIntelPage = useMatch({ from: CALL_INTEL_PATH, shouldThrow: false });

  const { isEligible, isLoading } = useProOnboardingEligibility();

  const { completedGroups, completeTask, flow } = useProOnboardingProgress({
    enabled: isEligible,
    trackLifecycle: isEligible && !isLoading,
  });

  const { isGuideOpen, toggleOnboardingGuide } = useOnboardingShallowStore('isGuideOpen', 'toggleOnboardingGuide');

  const [nudgeLifecycle] = usePreference('CIProChecklistNudgeLifecycle');

  const nudgeHandled = useRef(false);

  const hasInitialChecklistAutoOpenHappened = !!nudgeLifecycle.initialAutoOpenedAt;

  const { shouldShow: shouldNudge, recordDismiss } = useEligibility({
    featureId: CI_PRO_FEATURE_IDS.checklistNudge,
    defaultCriteria: {
      type: 'dismiss-cooldown',
      maxDismisses: CI_PRO_CHECKLIST_POLICY.maxNudges,
      cooldownDays: CI_PRO_CHECKLIST_POLICY.nudgeCooldownDays,
    },
  });

  const isChecklistCompleted = useMemo(() => {
    return flow.tasks.every((task) => completedGroups.includes(task.id as OnboardingProTask));
  }, [flow.tasks, completedGroups]);

  const openOnboardingGuide = useCallback(() => {
    if (!isGuideOpen) {
      toggleOnboardingGuide();
    }
  }, [isGuideOpen, toggleOnboardingGuide]);

  useRegisterOnboardingFlow({
    flow,
    enabled: isEligible,
    isLoading,
    completedGroups,
    onGroupComplete: completeTask,
  });

  useEffect(() => {
    if (isLoading || !isEligible) return;

    /**
     * The checklist can be manually opened from anywhere,
     * but automatic PRO nudge opens should only happen on Call Intelligence.
     */
    if (!isOnCallIntelPage) return;

    /**
     * Nudges should start only after the checklist first auto-opens from:
     * - announcement banner click
     * - announcement banner view-limit expiry
     */
    if (!hasInitialChecklistAutoOpenHappened) return;

    /**
     * Prevent duplicate nudge opens during the same component lifecycle.
     */
    if (nudgeHandled.current) return;

    /**
     * Respect max nudge count and cooldown.
     */
    if (!shouldNudge) return;

    /**
     * Do not nudge once checklist is complete.
     */
    if (isChecklistCompleted) return;

    nudgeHandled.current = true;
    recordDismiss();
    trackOnboardingEvent(CI_PRO_ONBOARDING_EVENTS.checklistNudgeOpened, {
      onboardingFlow: flow.id,
    });

    openOnboardingGuide();
  }, [
    isLoading,
    isEligible,
    shouldNudge,
    recordDismiss,
    openOnboardingGuide,
    isOnCallIntelPage,
    hasInitialChecklistAutoOpenHappened,
    isChecklistCompleted,
    flow.id,
  ]);

  return null;
}
